import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Alert, Box, Button, IconButton, Paper, Stack, Tooltip, Typography, alpha } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { AiChatContext } from './AiChatContext';
import AiChatConversationList from './AiChatConversationList';
import AiChatInput from './AiChatInput';
import AiChatMessageList from './AiChatMessageList';
import { isDepartmentSelectionQuestion } from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS } from './chatModes';

const PANEL_DEFAULT_OFFSET = 16;
const VIEWPORT_EDGE_PADDING = 0;
const DEFAULT_PANEL_WIDTH = 440;
const DEFAULT_PANEL_HEIGHT = 760;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 420;

function normalizeAllowlistValue(value: string): string {
  return value.trim().toLowerCase();
}

const OPENAI_CONVERSATION_ID_ALLOWED_USERS = new Set(
  [
    'william.ritossa@luxuryescapes.com',
    'joshua.paling@luxuryescapes.com',
    'braydn.evans@luxuryescapes.com',
    'luke.wang@luxuryescapes.com',
  ].map(normalizeAllowlistValue),
);
const noop = () => undefined;
const asyncNoop = async (): Promise<void> => Promise.resolve();
const asyncNull = async (): Promise<string | null> => null;

interface PanelPosition {
  x: number;
  y: number;
}

interface PanelSize {
  width: number;
  height: number;
}

interface PanelLayout {
  position: PanelPosition;
  size: PanelSize;
}

type ResizeDirection = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type ResizeEdges = { left?: boolean; right?: boolean; top?: boolean; bottom?: boolean; cursor: string };

const RESIZE_DIRECTIONS: Record<ResizeDirection, ResizeEdges> = {
  top: { top: true, cursor: 'ns-resize' },
  right: { right: true, cursor: 'ew-resize' },
  bottom: { bottom: true, cursor: 'ns-resize' },
  left: { left: true, cursor: 'ew-resize' },
  'top-left': { top: true, left: true, cursor: 'nwse-resize' },
  'top-right': { top: true, right: true, cursor: 'nesw-resize' },
  'bottom-left': { bottom: true, left: true, cursor: 'nesw-resize' },
  'bottom-right': { bottom: true, right: true, cursor: 'nwse-resize' },
};

const RESIZE_HANDLE_SX: Record<ResizeDirection, Record<string, number | string>> = {
  left: { top: 12, left: 0, bottom: 12, width: 12 },
  right: { top: 12, right: 0, bottom: 12, width: 12 },
  top: { left: 12, right: 12, top: 0, height: 12 },
  bottom: { left: 12, right: 12, bottom: 0, height: 12 },
  'top-left': { left: 0, top: 0, width: 18, height: 18 },
  'top-right': { right: 0, top: 0, width: 18, height: 18 },
  'bottom-left': { left: 0, bottom: 0, width: 18, height: 18 },
  'bottom-right': { right: 0, bottom: 0, width: 18, height: 18 },
};

function getDefaultPanelSize(): PanelSize {
  if (typeof window === 'undefined') {
    return { width: DEFAULT_PANEL_WIDTH, height: DEFAULT_PANEL_HEIGHT };
  }

  return {
    width: Math.min(DEFAULT_PANEL_WIDTH, window.innerWidth),
    height: Math.min(DEFAULT_PANEL_HEIGHT, window.innerHeight),
  };
}

function clampPanelPosition(position: PanelPosition, size: PanelSize): PanelPosition {
  if (typeof window === 'undefined') {
    return position;
  }

  const maxX = Math.max(VIEWPORT_EDGE_PADDING, window.innerWidth - size.width - VIEWPORT_EDGE_PADDING);
  const maxY = Math.max(VIEWPORT_EDGE_PADDING, window.innerHeight - size.height - VIEWPORT_EDGE_PADDING);

  return {
    x: Math.min(Math.max(position.x, VIEWPORT_EDGE_PADDING), maxX),
    y: Math.min(Math.max(position.y, VIEWPORT_EDGE_PADDING), maxY),
  };
}

function clampDimension(nextSize: number, minSize: number, maxSize: number): number {
  const effectiveMinSize = Math.min(minSize, maxSize);
  return Math.min(Math.max(nextSize, effectiveMinSize), maxSize);
}

function clampPanelSize(size: PanelSize, position: PanelPosition): PanelSize {
  if (typeof window === 'undefined') {
    return size;
  }

  const maxWidth = Math.max(window.innerWidth - position.x - VIEWPORT_EDGE_PADDING, 0);
  const maxHeight = Math.max(window.innerHeight - position.y - VIEWPORT_EDGE_PADDING, 0);

  return {
    width: clampDimension(size.width, MIN_PANEL_WIDTH, maxWidth),
    height: clampDimension(size.height, MIN_PANEL_HEIGHT, maxHeight),
  };
}

function getDefaultPanelPosition(size: PanelSize): PanelPosition {
  if (typeof window === 'undefined') {
    return { x: VIEWPORT_EDGE_PADDING, y: VIEWPORT_EDGE_PADDING };
  }

  return clampPanelPosition(
    {
      x: window.innerWidth - size.width - PANEL_DEFAULT_OFFSET,
      y: window.innerHeight - size.height - PANEL_DEFAULT_OFFSET,
    },
    size,
  );
}

function getDefaultPanelLayout(): PanelLayout {
  const size = getDefaultPanelSize();
  return { size, position: getDefaultPanelPosition(size) };
}

export default function AiChatDrawer() {
  const chatContext = useContext(AiChatContext);
  const { user: currentUser } = useCurrentUser();
  const panelLayoutRef = useRef<PanelLayout>(getDefaultPanelLayout());
  const [panelSize, setPanelSize] = useState<PanelSize>(() => panelLayoutRef.current.size);
  const [activeTab, setActiveTab] = useState<'chat' | 'conversations'>('chat');
  const [panelPosition, setPanelPosition] = useState<PanelPosition>(() => panelLayoutRef.current.position);
  const [isDragging, setIsDragging] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeOriginRef = useRef({
    pointerX: 0,
    pointerY: 0,
    panelX: 0,
    panelY: 0,
    width: DEFAULT_PANEL_WIDTH,
    height: DEFAULT_PANEL_HEIGHT,
  });
  const wasOpenRef = useRef(false);
  const userEmail = currentUser?.email?.trim().toLowerCase() || '';
  const canViewOpenaiConversationId = OPENAI_CONVERSATION_ID_ALLOWED_USERS.has(userEmail);
  const isOpen = chatContext?.isOpen ?? false;
  const threads = chatContext?.threads ?? {};
  const activeThreadId = chatContext?.activeThreadId ?? null;
  const contextActiveThread = chatContext?.activeThread ?? null;
  const sendingThreadIds = chatContext?.sendingThreadIds ?? [];
  const errorsByThreadId = chatContext?.errorsByThreadId ?? {};
  const globalError = chatContext?.globalError ?? null;
  const currentContextSnapshot = chatContext?.currentContextSnapshot ?? null;
  const closeChat = chatContext?.closeChat || noop;
  const sendMessage = chatContext?.sendMessage || asyncNoop;
  const stopStreaming = chatContext?.stopStreaming || noop;
  const openChat = chatContext?.openChat || asyncNoop;
  const createThread = chatContext?.createThread || asyncNull;
  const switchThread = chatContext?.switchThread || noop;
  const deleteThread = chatContext?.deleteThread || noop;

  const setPanelLayout = useCallback((layout: PanelLayout) => {
    panelLayoutRef.current = layout;
    setPanelPosition(layout.position);
    setPanelSize(layout.size);
  }, []);
  const handleDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target instanceof HTMLElement ? e.target : null;
    if (target?.closest('[data-ai-chat-window-action="true"]')) {
      return;
    }

    dragOffsetRef.current = {
      x: e.clientX - panelLayoutRef.current.position.x,
      y: e.clientY - panelLayoutRef.current.position.y,
    };
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    function handlePointerMove(e: PointerEvent) {
      setPanelLayout({
        position: clampPanelPosition(
          {
            x: e.clientX - dragOffsetRef.current.x,
            y: e.clientY - dragOffsetRef.current.y,
          },
          panelLayoutRef.current.size,
        ),
        size: panelLayoutRef.current.size,
      });
    }

    function handlePointerUp() {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, setPanelLayout]);

  const handleResizeStart = useCallback(
    (direction: ResizeDirection) => (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      resizeOriginRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        panelX: panelLayoutRef.current.position.x,
        panelY: panelLayoutRef.current.position.y,
        width: panelLayoutRef.current.size.width,
        height: panelLayoutRef.current.size.height,
      };
      setResizeDirection(direction);
      document.body.style.cursor = RESIZE_DIRECTIONS[direction].cursor;
      document.body.style.userSelect = 'none';
    },
    [],
  );

  useEffect(() => {
    if (!resizeDirection) {
      return undefined;
    }

    function handlePointerMove(e: PointerEvent) {
      const { pointerX, pointerY, panelX, panelY, width, height } = resizeOriginRef.current;
      const deltaX = e.clientX - pointerX;
      const deltaY = e.clientY - pointerY;
      const rightEdge = panelX + width;
      const bottomEdge = panelY + height;
      const { left, right, top, bottom } = RESIZE_DIRECTIONS[resizeDirection];

      const nextPosition: PanelPosition = { x: panelX, y: panelY };
      const nextSize: PanelSize = { width, height };

      if (left) {
        const maxX = Math.max(VIEWPORT_EDGE_PADDING, rightEdge - MIN_PANEL_WIDTH);
        nextPosition.x = Math.min(Math.max(panelX + deltaX, VIEWPORT_EDGE_PADDING), maxX);
        nextSize.width = rightEdge - nextPosition.x;
      }

      if (right && typeof window !== 'undefined') {
        nextSize.width = clampDimension(
          width + deltaX,
          MIN_PANEL_WIDTH,
          window.innerWidth - panelX - VIEWPORT_EDGE_PADDING,
        );
      }

      if (top) {
        const maxY = Math.max(VIEWPORT_EDGE_PADDING, bottomEdge - MIN_PANEL_HEIGHT);
        nextPosition.y = Math.min(Math.max(panelY + deltaY, VIEWPORT_EDGE_PADDING), maxY);
        nextSize.height = bottomEdge - nextPosition.y;
      }

      if (bottom && typeof window !== 'undefined') {
        nextSize.height = clampDimension(
          height + deltaY,
          MIN_PANEL_HEIGHT,
          window.innerHeight - panelY - VIEWPORT_EDGE_PADDING,
        );
      }

      setPanelLayout({ position: nextPosition, size: nextSize });
    }

    function handlePointerUp() {
      setResizeDirection(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizeDirection, setPanelLayout]);

  useEffect(() => {
    function syncPanelPosition() {
      const nextPosition = clampPanelPosition(panelLayoutRef.current.position, panelLayoutRef.current.size);
      setPanelLayout({
        position: nextPosition,
        size: clampPanelSize(panelLayoutRef.current.size, nextPosition),
      });
    }

    syncPanelPosition();
    window.addEventListener('resize', syncPanelPosition);

    return () => {
      window.removeEventListener('resize', syncPanelPosition);
    };
  }, [setPanelLayout]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setPanelLayout(getDefaultPanelLayout());
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, setPanelLayout]);
  const activeThread = contextActiveThread?.mode === 'default' ? contextActiveThread : null;
  const isLoading = activeThread ? sendingThreadIds.includes(activeThread.id) : false;
  const error = activeThread ? errorsByThreadId[activeThread.id] ?? null : null;
  const showLauncher = !isOpen;
  const defaultThreads = Object.fromEntries(Object.entries(threads).filter(([, thread]) => thread.mode === 'default'));

  const messages = activeThread?.messages || [];
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const pendingDepartmentSelection =
    !isLoading && lastMessage?.role === 'assistant' && isDepartmentSelectionQuestion(lastMessage.content);

  const handleSelectThread = useCallback(
    (threadId: string) => {
      switchThread(threadId);
      setActiveTab('chat');
    },
    [switchThread],
  );

  const handleNewChat = useCallback(async () => {
    await createThread('default', currentContextSnapshot);
    setActiveTab('chat');
  }, [createThread, currentContextSnapshot]);

  if (!chatContext) {
    return null;
  }

  return (
    <>
      {showLauncher && (
        <Button
          variant="contained"
          size="small"
          onClick={() => void openChat()}
          aria-label="Open AI chat"
          startIcon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 99999,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            py: 1.25,
            px: 0.75,
            minWidth: 0,
            minHeight: 110,
            borderRadius: '12px 0 0 12px',
            boxShadow: (theme) =>
              `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.55)}, 0 2px 6px -2px ${alpha(
                theme.palette.primary.main,
                0.3,
              )}`,
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            gap: 0.75,
            transition: 'transform 160ms ease-out, box-shadow 160ms ease-out',
            '&:hover': {
              transform: 'translateY(-50%) translateX(-2px)',
              boxShadow: (theme) =>
                `0 12px 30px -8px ${alpha(theme.palette.primary.main, 0.65)}, 0 4px 10px -2px ${alpha(
                  theme.palette.primary.main,
                  0.35,
                )}`,
            },
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
        >
          AI Chat
        </Button>
      )}

      {isOpen && (
        <Paper
          elevation={0}
          role="dialog"
          aria-label="AI chat panel"
          sx={{
            position: 'fixed',
            left: panelPosition.x,
            top: panelPosition.y,
            width: panelSize.width,
            height: panelSize.height,
            maxWidth: '100vw',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 3,
            border: 1,
            borderColor: (theme) => alpha(theme.palette.divider, 0.6),
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 24px 60px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.04)'
                : '0 24px 60px -16px rgba(15, 23, 42, 0.22), 0 8px 20px -10px rgba(15, 23, 42, 0.1)',
            zIndex: 1300,
          }}
        >
          <Box
            onPointerDown={handleDragStart}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              py: 1.25,
              borderBottom: 1,
              borderColor: (theme) => alpha(theme.palette.divider, 0.5),
              cursor: isDragging ? 'grabbing' : 'grab',
              bgcolor: 'background.paper',
              flexShrink: 0,
              touchAction: 'none',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.contrastText',
                flexShrink: 0,
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: (theme) =>
                    `radial-gradient(circle at 32% 28%, ${alpha(theme.palette.common.white, 0.45)}, transparent 55%)`,
                  pointerEvents: 'none',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.18)}`,
                  pointerEvents: 'none',
                },
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 15, position: 'relative', zIndex: 1 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, letterSpacing: -0.1 }} noWrap>
                Assistant
              </Typography>
              {activeThread?.openaiConversationId && canViewOpenaiConversationId && (
                <Tooltip title="Open in OpenAI logs" placement="bottom-start">
                  <Typography
                    component="a"
                    href={`https://platform.openai.com/logs/${activeThread.openaiConversationId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    data-ai-chat-window-action="true"
                    sx={{
                      display: 'block',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      fontSize: '0.65rem',
                      lineHeight: 1.4,
                      opacity: 0.7,
                      textDecoration: 'none',
                      '&:hover': { color: 'text.primary', opacity: 1 },
                    }}
                  >
                    {activeThread.openaiConversationId}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title={activeTab === 'chat' ? 'View history' : 'Back to chat'}>
                <IconButton
                  size="small"
                  onClick={() => setActiveTab(activeTab === 'chat' ? 'conversations' : 'chat')}
                  data-ai-chat-window-action="true"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06) },
                  }}
                >
                  {activeTab === 'chat' ? (
                    <HistoryRoundedIcon fontSize="small" />
                  ) : (
                    <ChatBubbleOutlineRoundedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {activeTab === 'chat' && messages.length > 0 ? (
                <Tooltip title="New chat">
                  <IconButton
                    size="small"
                    onClick={() => void handleNewChat()}
                    data-ai-chat-window-action="true"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06) },
                    }}
                  >
                    <AddCommentOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={closeChat}
                  data-ai-chat-window-action="true"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06) },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Alert
            severity="warning"
            variant="outlined"
            icon={false}
            sx={{
              mx: 1.5,
              mt: 1.25,
              mb: 0.5,
              py: 0.5,
              borderRadius: 2,
              borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              color: 'text.primary',
              flexShrink: 0,
              '& .MuiAlert-message': {
                py: 0.25,
                fontSize: '0.75rem',
                lineHeight: 1.45,
              },
            }}
          >
            AI Chat is new and still changing. Chat history will be wiped before release.
          </Alert>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {activeTab === 'conversations' ? (
              <AiChatConversationList
                threads={defaultThreads}
                activeThreadId={activeThreadId}
                onSelect={handleSelectThread}
                onDelete={deleteThread}
                onNewChat={() => void handleNewChat()}
              />
            ) : activeThread ? (
              <>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <AiChatMessageList
                    threadId={activeThread.id}
                    messages={messages}
                    isLoading={isLoading}
                    orderId={activeThread.latestContextSnapshot?.orderId ?? null}
                    department={activeThread.latestContextSnapshot?.department ?? null}
                  />
                </Box>

                {error && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{
                      mx: 2,
                      mb: 1,
                      borderRadius: 2,
                      py: 0.25,
                      fontSize: '0.8rem',
                      '& .MuiAlert-message': { py: 0.5 },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <AiChatInput
                  onSend={(content) => void sendMessage(activeThread.id, content, currentContextSnapshot)}
                  onStop={() => stopStreaming(activeThread.id)}
                  isLoading={isLoading}
                  selectionOptions={pendingDepartmentSelection ? DEPARTMENT_OPTIONS : []}
                  selectionLabel={pendingDepartmentSelection ? 'Choose your department' : null}
                />
              </>
            ) : (
              <>
                {globalError && (
                  <Alert severity="error" variant="outlined" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
                    {globalError}
                  </Alert>
                )}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    Select a conversation from history, or start a new one.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          {Object.entries(RESIZE_HANDLE_SX).map(([direction, handleSx]) => (
            <Box
              key={direction}
              onPointerDown={handleResizeStart(direction as ResizeDirection)}
              data-ai-chat-window-action="true"
              sx={{
                position: 'absolute',
                cursor: RESIZE_DIRECTIONS[direction as ResizeDirection].cursor,
                zIndex: direction.includes('-') ? 2 : 1,
                ...handleSx,
              }}
            />
          ))}
        </Paper>
      )}
    </>
  );
}
